import { PlexRoute } from '@all-hands-hq/plex-graphql-schema';
import MomentUtils from '@date-io/moment';
import { Checkbox, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MuiDone from '@material-ui/icons/Done';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import Moment from 'moment-timezone';
import React from 'react';

import { spacings } from '../../../theme';
import { PlexIconButton } from '../../shared/primitives/PlexIconButton';
import { getPlexRouteTimeRangeEnum, getTimeRange, TimeRangeEnum } from './TimeRange';

export type UpdateTimelineFilters = (args: Partial<PlexRoute['timelineFilters']>) => void;

type Props = {
  className?: string;
  start: Moment.Moment;
  finish: Moment.Moment;
  selectedTimeOption: TimeRangeEnum;
  updateTimelineFilters: UpdateTimelineFilters;
};

export const TimeRangeSelect = ({
  className,
  start,
  finish,
  selectedTimeOption,
  updateTimelineFilters,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const [localStart, setLocalStart] = React.useState(start);
  const [localFinish, setLocalFinish] = React.useState(finish);
  const [localSelectedTimeOption, setLocalSelectedTimeOption] = React.useState(TimeRangeEnum.ThisWeek);
  const [open, setOpen] = React.useState(false);

  let label: string;
  switch (selectedTimeOption) {
    case TimeRangeEnum.ThisWeek:
      label = 'This Week';
      break;
    case TimeRangeEnum.ThisMonth:
      label = 'This Month';
      break;
    case TimeRangeEnum.Custom:
      label = `${start.format('MM/DD')} -  ${finish.format('MM/DD')}`;
  }
  const visibleTimeRanges = [TimeRangeEnum.ThisWeek, TimeRangeEnum.ThisMonth] as const;

  return (
    <FormControl className={className}>
      <InputLabel id="time-range">Time range</InputLabel>
      <Select
        value={selectedTimeOption}
        open={open}
        onClick={(): void => {
          setOpen(!open);
          setLocalSelectedTimeOption(selectedTimeOption);
        }}
        renderValue={(): string => label}
      >
        {visibleTimeRanges.map((timeRange) => {
          const timeRangeData = getTimeRange(timeRange);
          return (
            <MenuItem
              key={timeRange}
              value={timeRange}
              divider={true}
              onClick={(): void => {
                updateTimelineFilters({
                  timeInterval: { type: getPlexRouteTimeRangeEnum(timeRange) },
                });
                setLocalSelectedTimeOption(timeRange);
              }}
            >
              <Checkbox color="primary" checked={localSelectedTimeOption === timeRange} />
              {timeRangeData.displayName}
            </MenuItem>
          );
        })}

        <MenuItem
          className={classes.customMenuItem}
          disableRipple={true}
          value={TimeRangeEnum.Custom}
          onClick={(ev: React.MouseEvent): void => {
            ev.stopPropagation();
            setLocalSelectedTimeOption(TimeRangeEnum.Custom);
          }}
        >
          <Checkbox color="primary" checked={localSelectedTimeOption === TimeRangeEnum.Custom} />
          <div>
            Custom
            <div className={classes.timeRangeContainer}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <KeyboardDatePicker
                  variant="inline"
                  format="YYYY/MM/DD"
                  label="From"
                  autoOk={true}
                  value={localStart}
                  onChange={(d: MaterialUiPickersDate): void | null => d && setLocalStart(d)}
                  KeyboardButtonProps={{
                    'aria-label': 'From',
                  }}
                  className={classes.datePicker}
                />
                <KeyboardDatePicker
                  variant="inline"
                  format="YYYY/MM/DD"
                  label="To"
                  autoOk={true}
                  value={localFinish}
                  onChange={(d: MaterialUiPickersDate): void | null => d && setLocalFinish(d)}
                  KeyboardButtonProps={{
                    'aria-label': 'To',
                  }}
                  className={classes.datePicker}
                />
              </MuiPickersUtilsProvider>
              <PlexIconButton
                MuiIcon={MuiDone}
                className={classes.noShrink}
                onClick={(): void => {
                  updateTimelineFilters({
                    timeInterval: {
                      type: 'custom',
                      start: localStart.toISOString(),
                      finish: localFinish.toISOString(),
                    },
                  });
                  setOpen(false);
                }}
              />
            </div>
          </div>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

const useStyles = makeStyles((theme) => ({
  customMenuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  timeRangeContainer: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  datePicker: {
    width: 150,
    marginRight: spacings.normal,
  },
  noShrink: {
    flexShrink: 0,
    width: 36,
    height: 36,
  },
}));
